import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveOrg } from "@/hooks/useActiveOrg";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, User, BookOpen, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import InteractiveSchedulingCalendar from "@/components/scheduling/InteractiveSchedulingCalendar";

interface StudentEmail {
  email: string;
  student_name: string | null;
}

interface Subject {
  id: string;
  name: string;
  grade: string;
}

export default function InstructorCreateTutoringSchedule() {
  const { user } = useAuth();
  const { activeOrgId } = useActiveOrg();
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const isEditMode = !!scheduleId;
  const [loading, setLoading] = useState(false);
  const [studentEmails, setStudentEmails] = useState<StudentEmail[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [availableGrades] = useState(['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);
  const [formData, setFormData] = useState({
    student_email: '',
    student_name: '',
    grade: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    subject: '',
    topic: '',
    notes: '',
    recurrence_pattern: 'once',
    recurrence_days: [] as number[],
    send_notifications: true
  });

  useEffect(() => {
    loadStudentEmails();
    loadSubjects();
    if (isEditMode && scheduleId) {
      loadExistingSchedule();
    }
  }, [user, scheduleId, isEditMode]);

  const loadExistingSchedule = async () => {
    if (!scheduleId) return;

    try {
      const { data, error } = await supabase
        .from('tutoring_schedules')
        .select('*')
        .eq('id', scheduleId)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          student_email: data.student_email,
          student_name: data.student_name || '',
          grade: '', // We'll need to derive this from subject
          start_date: data.start_date || data.session_date,
          end_date: data.end_date || data.session_date,
          start_time: data.start_time,
          end_time: data.end_time,
          subject: data.subject,
          topic: data.topic || '',
          notes: data.notes || '',
          recurrence_pattern: data.recurrence_pattern || 'once',
          recurrence_days: data.recurrence_days || [],
          send_notifications: true
        });
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      toast.error('Failed to load schedule data');
      navigate('/instructor/tutoring');
    }
  };

  const loadStudentEmails = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('instructor_student_emails')
        .select(`
          email,
          student_name,
          instructor_student_lists!inner(instructor_id)
        `)
        .eq('instructor_student_lists.instructor_id', user.id);

      if (error) throw error;

      const uniqueEmails = Array.from(
        new Map(data.map(item => [item.email, item])).values()
      );
      
      setStudentEmails(uniqueEmails);
    } catch (error) {
      console.error('Error loading student emails:', error);
      toast.error('Failed to load student list');
    }
  };

  const loadSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('id, name, grade')
        .order('grade', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setSubjects(data || []);
    } catch (error) {
      console.error('Error loading subjects:', error);
      toast.error('Failed to load subjects');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-fill student name when email is selected
    if (field === 'student_email') {
      const selectedStudent = studentEmails.find(s => s.email === value);
      if (selectedStudent && selectedStudent.student_name) {
        setFormData(prev => ({
          ...prev,
          student_name: selectedStudent.student_name || ''
        }));
      }
    }
  };

  const validateForm = () => {
    if (!formData.student_email) {
      toast.error('Please select a student');
      return false;
    }
    if (!formData.start_date) {
      toast.error('Please select a start date');
      return false;
    }
    if (!formData.end_date) {
      toast.error('Please select an end date');
      return false;
    }
    if (!formData.start_time || !formData.end_time) {
      toast.error('Please set start and end times');
      return false;
    }
    if (formData.start_time >= formData.end_time) {
      toast.error('End time must be after start time');
      return false;
    }
    if (!formData.grade) {
      toast.error('Please select a grade');
      return false;
    }
    if (!formData.subject) {
      toast.error('Please select a subject');
      return false;
    }

    // Check if dates are in the past
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (startDate < today) {
      toast.error('Cannot schedule sessions in the past');
      return false;
    }

    if (endDate < startDate) {
      toast.error('End date must be after start date');
      return false;
    }

    return true;
  };

  const generateRecurringSessions = () => {
    const sessions = [];
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    
    if (formData.recurrence_pattern === 'once') {
      sessions.push({
        session_date: formData.start_date,
        start_date: formData.start_date,
        end_date: formData.end_date
      });
    } else if (formData.recurrence_pattern === 'daily') {
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        sessions.push({
          session_date: currentDate.toISOString().split('T')[0],
          start_date: formData.start_date,
          end_date: formData.end_date
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (formData.recurrence_pattern === 'weekly') {
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        sessions.push({
          session_date: currentDate.toISOString().split('T')[0],
          start_date: formData.start_date,
          end_date: formData.end_date
        });
        currentDate.setDate(currentDate.getDate() + 7);
      }
    }
    
    console.log(`Generated ${sessions.length} sessions for ${formData.recurrence_pattern} recurrence`);
    return sessions;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) return;

    setLoading(true);

    try {
      // Get selected subject name
      const selectedSubject = subjects.find(s => s.id === formData.subject);
      const subjectName = selectedSubject?.name || formData.subject;

      if (isEditMode && scheduleId) {
        // Update existing schedule
        const { error } = await supabase
          .from('tutoring_schedules')
          .update({
            student_email: formData.student_email,
            student_name: formData.student_name || null,
            start_date: formData.start_date,
            end_date: formData.end_date,
            start_time: formData.start_time,
            end_time: formData.end_time,
            subject: subjectName,
            topic: formData.topic || null,
            notes: formData.notes || null,
            recurrence_pattern: formData.recurrence_pattern,
            recurrence_days: formData.recurrence_days.length > 0 ? formData.recurrence_days : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', scheduleId);

        if (error) throw error;

        // Send update notification if enabled
        if (formData.send_notifications) {
          try {
            const { data: instructorProfile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            const instructorName = instructorProfile 
              ? `${instructorProfile.first_name || ''} ${instructorProfile.last_name || ''}`.trim()
              : 'Your Instructor';

            const recipients: Array<{ email: string; name?: string; role: 'student' | 'parent' | 'instructor' }> = [
              {
                email: formData.student_email,
                name: formData.student_name,
                role: 'student'
              }
            ];

            await supabase.functions.invoke('send-schedule-notification', {
              body: {
                scheduleId,
                type: 'updated',
                recipients,
                scheduleData: {
                  student_name: formData.student_name || formData.student_email,
                  student_email: formData.student_email,
                  session_date: formData.start_date,
                  start_time: formData.start_time,
                  end_time: formData.end_time,
                  subject: subjectName,
                  topic: formData.topic,
                  notes: formData.notes,
                  instructor_name: instructorName
                }
              }
            });
          } catch (notificationError) {
            console.error('Error sending update notification:', notificationError);
            toast.warning('Session updated successfully, but notification may not have been sent');
          }
        }

        toast.success('Tutoring session updated successfully!');
      } else {
        // Create new schedule(s)
        const sessions = generateRecurringSessions();
        
        const sessionInserts = sessions.map(session => ({
          instructor_id: user.id,
          org_id: activeOrgId ?? null,
          student_email: formData.student_email,
          student_name: formData.student_name || null,
          session_date: session.session_date,
          start_date: session.start_date,
          end_date: session.end_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          subject: subjectName,
          topic: formData.topic || null,
          notes: formData.notes || null,
          recurrence_pattern: formData.recurrence_pattern,
          recurrence_days: formData.recurrence_days.length > 0 ? formData.recurrence_days : null,
          status: 'scheduled'
        }));

        const { error } = await supabase
          .from('tutoring_schedules')
          .insert(sessionInserts);

        if (error) throw error;

        // Send notifications if enabled
        if (formData.send_notifications) {
          try {
            const { data: instructorProfile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            const instructorName = instructorProfile 
              ? `${instructorProfile.first_name || ''} ${instructorProfile.last_name || ''}`.trim()
              : 'Your Instructor';

            const { data: parentData } = await supabase
              .from('children')
              .select('parents(email, first_name, last_name)')
              .eq('student_email', formData.student_email);

            const recipients: Array<{ email: string; name?: string; role: 'student' | 'parent' | 'instructor' }> = [
              {
                email: formData.student_email,
                name: formData.student_name,
                role: 'student'
              }
            ];

            if (parentData) {
              parentData.forEach(child => {
                if (child.parents) {
                  recipients.push({
                    email: child.parents.email,
                    name: `${child.parents.first_name || ''} ${child.parents.last_name || ''}`.trim(),
                    role: 'parent'
                  });
                }
              });
            }

            const { data: createdSessions, error: fetchError } = await supabase
              .from('tutoring_schedules')
              .select('id, session_date, start_time, end_time')
              .eq('instructor_id', user.id)
              .eq('student_email', formData.student_email)
              .in('session_date', sessions.map(s => s.session_date))
              .order('created_at', { ascending: false })
              .limit(sessions.length);

            if (!fetchError && createdSessions) {
              for (const session of createdSessions) {
                await supabase.functions.invoke('send-schedule-notification', {
                  body: {
                    scheduleId: session.id,
                    type: 'created',
                    recipients,
                    scheduleData: {
                      student_name: formData.student_name || formData.student_email,
                      student_email: formData.student_email,
                      session_date: session.session_date,
                      start_time: session.start_time,
                      end_time: session.end_time,
                      subject: subjectName,
                      topic: formData.topic,
                      notes: formData.notes,
                      instructor_name: instructorName
                    }
                  }
                });
              }
            }
          } catch (notificationError) {
            console.error('Error sending notifications:', notificationError);
            toast.warning('Sessions created successfully, but some notifications may not have been sent');
          }
        }

        toast.success(`${sessions.length} tutoring session(s) scheduled successfully!`);
      }
      
      navigate('/instructor/tutoring');
    } catch (error) {
      console.error('Error with schedule:', error);
      toast.error(isEditMode ? 'Failed to update session' : 'Failed to schedule session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {isEditMode ? 'Edit Tutoring Session' : 'Schedule Tutoring Session'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? 'Update your tutoring session details' : 'Create a new tutoring session with a student'}
              </p>
            </div>
          </div>

          <Tabs defaultValue="form" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="form">Schedule Form</TabsTrigger>
              <TabsTrigger value="calendar">Interactive Calendar</TabsTrigger>
            </TabsList>

            <TabsContent value="calendar" className="mt-6">
              <InteractiveSchedulingCalendar 
                existingSchedules={[]} 
                onScheduleCreated={() => {
                  toast.success('Session scheduled successfully!');
                  navigate('/instructor/tutoring');
                }}
              />
            </TabsContent>

            <TabsContent value="form" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Session Details
                  </CardTitle>
                  <CardDescription>
                    {isEditMode ? 'Update the details for your tutoring session' : 'Fill in the details for your tutoring session'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                {/* Student Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="student_email" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Student Email *
                    </Label>
                    <Select value={formData.student_email} onValueChange={(value) => handleInputChange('student_email', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select student" />
                      </SelectTrigger>
                      <SelectContent>
                        {studentEmails.map((student) => (
                          <SelectItem key={student.email} value={student.email}>
                            {student.student_name ? `${student.student_name} (${student.email})` : student.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="student_name">Student Name</Label>
                    <Input
                      id="student_name"
                      value={formData.student_name}
                      onChange={(e) => handleInputChange('student_name', e.target.value)}
                      placeholder="Enter student name (optional)"
                    />
                  </div>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date" className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Start Date *
                    </Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date">
                      End Date *
                    </Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleInputChange('end_date', e.target.value)}
                      min={formData.start_date || new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recurrence_pattern">
                      Recurrence
                    </Label>
                    <Select value={formData.recurrence_pattern} onValueChange={(value) => handleInputChange('recurrence_pattern', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select recurrence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">One-time</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Start Time *
                    </Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => handleInputChange('start_time', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => handleInputChange('end_time', e.target.value)}
                    />
                  </div>
                </div>

                {/* Grade Selection */}
                <div className="space-y-2">
                  <Label htmlFor="grade" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Grade *
                  </Label>
                  <Select value={formData.grade} onValueChange={(value) => handleInputChange('grade', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableGrades.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          Grade {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subject and Topic */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Select 
                      value={formData.subject} 
                      onValueChange={(value) => handleInputChange('subject', value)}
                      disabled={!formData.grade}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={formData.grade ? "Select subject" : "Select grade first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects
                          .filter(subject => subject.grade === formData.grade)
                          .map((subject) => (
                            <SelectItem key={subject.id} value={subject.id}>
                              {subject.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic</Label>
                    <Input
                      id="topic"
                      value={formData.topic}
                      onChange={(e) => handleInputChange('topic', e.target.value)}
                      placeholder="e.g., Algebra, Essay Writing"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Any additional notes or preparation instructions..."
                    rows={3}
                  />
                </div>

                {/* Notification Options */}
                <div className="space-y-4">
                  <Label className="flex items-center gap-2 text-base font-medium">
                    <Bell className="h-4 w-4" />
                    Notification Options
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="send_notifications" 
                      checked={formData.send_notifications}
                      onCheckedChange={(checked) => 
                        handleInputChange('send_notifications', checked.toString())
                      }
                    />
                    <Label htmlFor="send_notifications" className="text-sm">
                      Send email notifications to student and parents
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, students and their registered parents will receive email notifications about this tutoring session.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={loading} className="flex-1">
                    {loading 
                      ? (isEditMode ? "Updating..." : "Scheduling...") 
                      : (isEditMode ? "Update Session" : "Schedule Session")
                    }
                  </Button>
                  <Button variant="outline" type="button" asChild>
                    <Link to="/instructor/tutoring">Cancel</Link>
                  </Button>
                </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}